import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Heading,
  HStack,
  IconButton,
  Input,
  List,
  ListItem,
  Spinner,
  Stack,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import { useCallback, useEffect, useState } from 'react';
import { FaTrash } from 'react-icons/fa6';
import { toast } from 'react-toastify';
import {
  addStoreGroupInstallations,
  listStoreGroupInstallations,
  removeStoreGroupInstallations,
} from 'services/storeGroup';
import type { StoreGroupInstallation } from 'services/interfaces/storeGroup';

interface ManageInstallationsSectionProps {
  storeGroupId: number;
  canUpdate: boolean;
  onCountChange?: (count: number) => void;
}

const ManageInstallationsSection: React.FC<ManageInstallationsSectionProps> = ({
  storeGroupId,
  canUpdate,
  onCountChange,
}) => {
  const bgColor = useColorModeValue('white', 'gray.800');
  const [installations, setInstallations] = useState<StoreGroupInstallation[]>(
    [],
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [newIdi, setNewIdi] = useState('');
  const [adding, setAdding] = useState(false);
  const [removingIdi, setRemovingIdi] = useState<number | null>(null);

  const loadInstallations = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listStoreGroupInstallations(storeGroupId);
      setInstallations(data);
      onCountChange?.(data.length);
    } catch {
      setError('Não foi possível carregar as lojas do grupo.');
    } finally {
      setLoading(false);
    }
  }, [storeGroupId, onCountChange]);

  useEffect(() => {
    loadInstallations();
  }, [loadInstallations]);

  const handleAdd = async () => {
    const idi = Number(newIdi);
    if (!idi || idi <= 0) {
      toast.error('Informe um IDI válido.');
      return;
    }
    setAdding(true);
    try {
      await addStoreGroupInstallations(storeGroupId, [idi]);
      setNewIdi('');
      toast.success('Loja adicionada ao grupo.');
      await loadInstallations();
    } catch (e) {
      const message =
        (e as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? 'Falha ao adicionar a loja ao grupo.';
      toast.error(message);
    } finally {
      setAdding(false);
    }
  };

  const handleRemove = async (idi: number) => {
    setRemovingIdi(idi);
    try {
      await removeStoreGroupInstallations(storeGroupId, [idi]);
      toast.success('Loja removida do grupo.');
      await loadInstallations();
    } catch {
      toast.error('Falha ao remover a loja do grupo.');
    } finally {
      setRemovingIdi(null);
    }
  };

  return (
    <Box bg={bgColor} borderRadius="lg" shadow="sm" p="1.5rem">
      <Heading size="md" color="gray.600" mb={4}>
        Lojas participantes
      </Heading>
      <Stack spacing={4}>
        {canUpdate && (
          <HStack>
            <Input
              placeholder="IDI da loja"
              value={newIdi}
              type="number"
              isDisabled={adding}
              onChange={(e) => setNewIdi(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
            />
            <Button
              colorScheme="yooga"
              onClick={handleAdd}
              isLoading={adding}
              isDisabled={!newIdi.trim()}
              flexShrink={0}
            >
              Adicionar
            </Button>
          </HStack>
        )}

        {error && (
          <Alert status="error" borderRadius="md">
            <AlertIcon />
            {error}
          </Alert>
        )}

        {loading ? (
          <HStack justify="center" py={6}>
            <Spinner size="md" color="yooga.500" />
          </HStack>
        ) : (
          <List spacing={2} maxH="300px" overflowY="auto">
            {installations.length === 0 && !error && (
              <Text color="gray.500" fontSize="sm" py={4} textAlign="center">
                Nenhuma loja associada a este grupo ainda.
              </Text>
            )}
            {installations.map((installation) => (
              <ListItem
                key={installation.idi}
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                borderWidth="1px"
                borderRadius="md"
                px={3}
                py={2}
              >
                <Text fontSize="sm">
                  {installation.apelido || 'Sem apelido'} · IDI{' '}
                  {installation.idi}
                </Text>
                {canUpdate && (
                  <IconButton
                    aria-label="Remover loja do grupo"
                    icon={<FaTrash />}
                    size="sm"
                    variant="ghost"
                    colorScheme="red"
                    isLoading={removingIdi === installation.idi}
                    onClick={() => handleRemove(installation.idi)}
                  />
                )}
              </ListItem>
            ))}
          </List>
        )}
      </Stack>
    </Box>
  );
};

export default ManageInstallationsSection;
